import { useEffect } from 'react';
import type { FilterCriteria, SortCriteria } from '@/types/search';
import type { FilterValues } from '@/components/organisms/tee-filter';

/**
 * Distinct per-page sessionStorage keys — a single descriptive string literal
 * per page, matching this package's existing `PAYING_FILTER_STORAGE_KEY`
 * naming convention. Intentionally NOT a shared namespace object so the
 * per-page persisted state can never collide.
 */
export const MEMBERS_LIST_STATE_KEY = 'box-os-members-list-state';
export const LOCATIONS_LIST_STATE_KEY = 'box-os-locations-list-state';
export const PLANS_LIST_STATE_KEY = 'box-os-plans-list-state';

/**
 * Shape of the list state persisted to sessionStorage for a given list page.
 * Only `page`/`pageSize` are carried for pagination — the server-derived
 * count fields on the `Pagination` type must never be restored.
 */
export interface PersistedListState {
  searchTerm: string;
  filters: Record<string, FilterCriteria>;
  filterValues: FilterValues;
  pagination: { page: number; pageSize: number };
  sort?: SortCriteria[];
  sortValue?: string;
}

/**
 * Pure, synchronous, side-effect-free reader — safe to call inside a lazy
 * `useState` initializer. Never throws: a blocked/unavailable sessionStorage
 * (private browsing, disabled storage) or a corrupted/hostile stored payload
 * both degrade to `null` (i.e. "nothing to restore, use defaults").
 *
 * Fields are rebuilt by ALLOW-LIST, never by spreading the parsed value, so
 * a tampered payload cannot inject arbitrary keys into page state.
 */
export function readPersistedListState(
  storageKey: string
): Partial<PersistedListState> | null {
  try {
    const raw = window.sessionStorage.getItem(storageKey);
    if (!raw) {
      return null;
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      // Corrupted/truncated JSON — treat as nothing stored.
      return null;
    }

    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      return null;
    }

    const source = parsed as Record<string, unknown>;
    const result: Partial<PersistedListState> = {};

    if (typeof source.searchTerm === 'string') {
      result.searchTerm = source.searchTerm;
    }

    if (
      typeof source.filters === 'object' &&
      source.filters !== null &&
      !Array.isArray(source.filters)
    ) {
      result.filters = source.filters as Record<string, FilterCriteria>;
    }

    if (
      typeof source.filterValues === 'object' &&
      source.filterValues !== null &&
      !Array.isArray(source.filterValues)
    ) {
      result.filterValues = source.filterValues as FilterValues;
    }

    if (
      typeof source.pagination === 'object' &&
      source.pagination !== null &&
      !Array.isArray(source.pagination)
    ) {
      const rawPagination = source.pagination as Record<string, unknown>;
      const page = rawPagination.page;
      const pageSize = rawPagination.pageSize;
      if (
        typeof page === 'number' &&
        Number.isInteger(page) &&
        page >= 1 &&
        typeof pageSize === 'number' &&
        Number.isInteger(pageSize) &&
        pageSize >= 1
      ) {
        // Only page/pageSize are carried through — the server-derived
        // count fields on the Pagination type are never restored.
        result.pagination = { page, pageSize };
      }
      // If page/pageSize fail validation, omit pagination entirely rather
      // than partially restoring it.
    }

    if (Array.isArray(source.sort)) {
      const validSort: SortCriteria[] = [];
      let allValid = true;
      for (const entry of source.sort) {
        if (
          typeof entry === 'object' &&
          entry !== null &&
          !Array.isArray(entry) &&
          typeof (entry as Record<string, unknown>).field === 'string' &&
          ((entry as Record<string, unknown>).order === 'asc' ||
            (entry as Record<string, unknown>).order === 'desc')
        ) {
          const e = entry as Record<string, unknown>;
          // Rebuilt by allow-list, not spread.
          validSort.push({
            field: e.field as string,
            order: e.order as 'asc' | 'desc',
          });
        } else {
          allValid = false;
          break;
        }
      }
      if (allValid && validSort.length === source.sort.length) {
        result.sort = validSort;
      }
    }

    if (typeof source.sortValue === 'string') {
      result.sortValue = source.sortValue;
    }

    if (Object.keys(result).length === 0) {
      // Nothing useful survived validation.
      return null;
    }

    return result;
  } catch {
    // Blocked/unavailable sessionStorage (private browsing, disabled
    // storage) must never throw and never break the page.
    return null;
  }
}

/**
 * Writer hook — mirrors the supplied state into sessionStorage under
 * `storageKey` whenever its serialized content changes. A blocked/unavailable
 * sessionStorage silently no-ops rather than breaking the list.
 */
export function useListPersistedState(
  storageKey: string,
  state: Partial<PersistedListState>
): void {
  // Computed fresh every render (not memoized on `state`'s object identity,
  // since callers pass a fresh object literal on every render) — the effect
  // below depends on this serialized STRING so it only fires when the
  // persisted content actually changes.
  let serialized: string | null;
  try {
    serialized = JSON.stringify(state);
  } catch {
    // Circular value or other serialization failure — nothing to persist.
    serialized = null;
  }

  useEffect(() => {
    if (serialized === null) {
      return;
    }
    try {
      window.sessionStorage.setItem(storageKey, serialized);
    } catch {
      // Blocked storage (private browsing, quota exceeded) must never break
      // the list — silently no-op.
    }
  }, [storageKey, serialized]);
}
